import { useState, FC, useContext } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme, IconButton, useMediaQuery, Box, Grid, Typography, ButtonBase } from "@mui/material";
import { FiHome, FiUser, FiCode } from "react-icons/fi";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";

import SearchBooking, { SearchContext } from "../search/searchBooking";
import { SessionContext } from "../../../../utility/useSession";
import UserDetailDialog from "../../../../components/users/UserDetailDialog";
import Updates from "./updates/updates";
import { User, TenantType } from "../../../../logic/api";
import CarParkSwitcher from "../carParkSwitcher/CarParkSwitcher";
import AlertsView from "../alertsView/AlertsView";
import AlertsViewV2 from "../alertsView/AlertsViewV2";
import { HelpCentre } from "../../../../components/icons/help-centre";
import { ReleaseFlag, useFeatureVariant, useReleaseManagement } from "../../../../utility/feature-management";
import { useRoles } from "@/components/users/rolesAndPermissions/useRoles";


interface IDataDisplayProps {
  heading: string;
  value?: string;
  icon?: React.ReactNode;
}

const DataDisplay: FC<IDataDisplayProps> = ({ heading, value, icon }) => {
  return (
    <>
      {icon}
      <Box>
        <Typography variant="caption" color="textSecondary">
          {heading}
        </Typography>
        <Typography textTransform="capitalize" fontSize="small" fontWeight="bold" color="textPrimary">
          {value}
        </Typography>
      </Box>
    </>
  );
}

interface IHeaderProps {
  showNav: boolean;
  setShowNav: (value: boolean) => void;
}

const Header: FC<IHeaderProps> = ({ showNav, setShowNav }) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isDesktopWidth = useMediaQuery(theme.breakpoints.up("md"));
  const searchContext = useContext(SearchContext);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showCarParkSwitcher, setShowCarParkSwitcher] = useState(false);
  const [userDialogUser, setUserDialogUser] = useState<User | null>(null);

  const headerLinkFlag = useReleaseManagement(ReleaseFlag.releaseHelpCentreLinkForHeader);
  const { data: rolesData } = useRoles();
  const availableRoles = rolesData?.roles || [];

  const isAlertsHookRefactorReleased = useReleaseManagement(ReleaseFlag.releaseAlertsHookRefactor);
  const isMobileNavReleased = useReleaseManagement(ReleaseFlag.releaseHeaderV2MobileNav);

  if (session.current.site == null) {
    return null;
  }

  const endSearch = () => {
    if (searchContext.setCriteria) {
      searchContext.setCriteria({
        search: "",
        cancelled: false,
        exact: false,
        show: false,
      });
    }
    setShowNav(true);
  };

  let showSearch = false;
  if (searchContext.criteria) {
    showSearch = searchContext.criteria.show;
  }

  let currentSite = "";
  const isUATTenant = session.current.site.tenant.type === TenantType.UAT
  if (session.current.site.home != null) {
    if (session.current.credential != null) {
      currentSite = session.current.credential.database;
    }
    currentSite = `${currentSite} (${session.current.site.home.name})`;

    if (isUATTenant) {
      currentSite = `${currentSite} - UAT`;
    }
  }

  const displayUserDialog = () => {
    if (session.current.site == null) {
      return;
    }
    setUserDialogUser(JSON.parse(JSON.stringify(session.current.site.user)));
    setShowUserDialog(true);
  };

  const userName = session.current.site.user ? `${session.current.site.user.firstName} ${session.current.site.user.lastName}` : ''

  return (
    <>
      {session.current.site != null && (
        <>
          <UserDetailDialog
            show={showUserDialog}
            user={userDialogUser}
            availableRoles={availableRoles}
            onClose={(didUpdate, user) => {
              setShowUserDialog(false);
              if (didUpdate) {
                if (session.current.site) {
                  if (user) {
                    session.setUser(user);
                  }
                }
              }
            }}
          />
          <CarParkSwitcher
            show={showCarParkSwitcher}
            onClose={() => {
              setShowCarParkSwitcher(false);
            }}
          />
        </>
      )}
      <Box
        component="header"
        borderBottom={1}
        borderColor="border.main"
        bgcolor={isUATTenant ? "#f7dcbe" : "nav.main"}
        p={1}
      >
        <Grid container columns={6} spacing={1} alignItems="center" justifyContent="space-between">
          {(!isDesktopWidth && !showSearch && !showNav) && (
            <IconButton onClick={() => setShowNav(true)}>
              <MenuIcon />
            </IconButton>
          )}

          {(isDesktopWidth || (!isDesktopWidth && showSearch)) && (
            <Grid container alignItems="center" columns={2} size={!isDesktopWidth ? "grow" : "auto"}>
              {showSearch && (
                <IconButton onClick={() => endSearch()}>
                  <CloseIcon />
                </IconButton>
              )}
              <Grid size="grow">
                <SearchBooking />
              </Grid>
            </Grid>
          )}

          {(!showSearch) && (
            <Grid size="grow" container columns={2} justifyContent={isDesktopWidth ? "flex-end" : "space-between"} alignItems="center">
              {isDesktopWidth ? (
                <Grid container columns={3} gap={1} direction="row" alignItems="center" justifyContent="flex-end">
                  {(session.options.environment !== "production") && (
                    <DataDisplay
                      icon={<FiCode size={24} color="inherit" />}
                      heading={t("home.environment")}
                      value={session.options.environment}
                    />
                  )}
                  <ButtonBase sx={{ borderRadius: 2, p: 1, gap: 1, textAlign: 'left' }} onClick={displayUserDialog}>
                    <DataDisplay
                      icon={<FiUser size={24} color="inherit" />}
                      heading={t("home.loggedInAs")}
                      value={userName}
                    />
                  </ButtonBase>
                  <ButtonBase sx={{ borderRadius: 2, p: 1, gap: 1, textAlign: 'left' }} onClick={() => setShowCarParkSwitcher(true)}>
                    <DataDisplay
                      icon={<FiHome size={24} color="inherit" />}
                      heading={t("home.workingAtSite")}
                      value={currentSite}
                    />
                  </ButtonBase>
                </Grid>
              ) : (
                <DataDisplay
                  heading={userName}
                  value={currentSite}
                />
              )}

              <Grid container columns={3} spacing={1} justifyContent="flex-end" alignItems="center">
                {!isDesktopWidth && isMobileNavReleased && (
                  <>
                    <IconButton onClick={displayUserDialog} aria-label={t("home.loggedInAs")}>
                      <FiUser size={24} />
                    </IconButton>
                    <IconButton onClick={() => setShowCarParkSwitcher(true)} aria-label={t("home.workingAtSite")}>
                      <FiHome size={24} />
                    </IconButton>
                  </>
                )}
                {isAlertsHookRefactorReleased ? <AlertsViewV2 /> : <AlertsView />}
                {headerLinkFlag && <HelpCentre />}
                <Updates />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

Header.propTypes = {
  setShowNav: propTypes.func.isRequired,
  showNav: propTypes.bool.isRequired,
};

export default Header;
